import map from "lodash/map";

export const fixQuill = (text) => {
    const pTagRegex = /(<p><br><\/p>)/ig;
    return text?.replace(pTagRegex, "") || "";
};

export const convertReturnsToHTML = (text) => {
    const returnNRegex = /(\n)/ig;
    return `<p>${text.replace(returnNRegex, "<br />")}</p>`;
};

export const charLimit = (text, maxLength, isHTML) => {
    if ((text || "").length > maxLength) {
        if(!isHTML) {
            return `${text.substring(0, maxLength - 1)}…`;
        }

        // isHTML
        let mention = text.indexOf("class=\"mention\"");
        let hashtag = text.indexOf("class=\"hashtag\"");
        let totalMaxLength = maxLength;
        if ((mention > -1 && mention < maxLength) || (hashtag > -1 && hashtag < maxLength)) {
            // there is a mention in the first 200 characters, add more to get past the crazy HTML
            totalMaxLength = maxLength + 275;
        }

        // Return all if it's less than max
        if(totalMaxLength >= text.length) {
            return text;
        }

        // If there's not a </p> tag, just return the max
        let pIndex = text.indexOf("</p>");
        if(pIndex < 0) {
            return `${text.substring(0, totalMaxLength - 1)}…`;
        }

        // Loop through looking for all </p> and stop once it's past totalMaxLength
        let lastIndex = pIndex;
        while(lastIndex < totalMaxLength ) {
            pIndex = text.substring(lastIndex+5).indexOf("</p>");
            if(pIndex < 0) {
                // No more matches
                break;
            }

            // Update the last index
            lastIndex = lastIndex + 5 + pIndex;
        }

        if(lastIndex > totalMaxLength + 20) {
            return `${text.substring(0, totalMaxLength - 1)}…</p>`;
        } else {
            return `${text.substring(0, lastIndex)}…</p>`;
        }
    }

    return text;
}

export const shortenFilename = (filename, maxLength) => {
    if(!filename) {
        return "";
    }
    const parts = filename.split(".");
    const shortened = charLimit(parts[0], maxLength-4, false);
    return `${shortened}.${parts[1]}`;
}

export const isValidEmailAddress = (email="") => {
    const format1 = /^[ A-Za-z0-9_@./#&+-]*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email);
    const format2 = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/.test(email);
    return format1 || format2;
}

export const getOptions = (collection) => {
    return map(collection, item => {
        return {
            name: item.name,
            value: item.id,
        };
    });
};

export const convertStripePrice = (price) => {
    return`$${Math.abs(price / 100).toFixed(2)}`
}

/**
 * Extracts plain text from HTML, converting mentions to readable format.
 * Handles truncated HTML gracefully and converts mention spans to "@username" format.
 * 
 * @param {string} htmlText - HTML string that may contain mentions and other HTML tags
 * @returns {string} Plain text with mentions converted to "@username" format
 */
export const extractTextFromHTML = (htmlText) => {
    if (!htmlText) {
        return "";
    }

    let text = htmlText;

    // First, convert mention spans to readable text
    // Match complete mention spans: <span class="mention" ...>@username</span>
    const completeMentionRegex = /<span[^>]*class=["'][^"']*mention[^"']*["'][^>]*>([^<]*)<\/span>/gi;
    text = text.replace(completeMentionRegex, (match, mentionText) => {
        // If mentionText is empty, try to extract from data-value attribute
        if (!mentionText || mentionText.trim() === "") {
            const dataValueMatch = match.match(/data-value=["']([^"']+)["']/i);
            if (dataValueMatch) {
                return `@${dataValueMatch[1]}`;
            }
            // If no data-value, check if there's a @ symbol in the match
            const atSymbolMatch = match.match(/@([^\s<"']+)/);
            if (atSymbolMatch) {
                return `@${atSymbolMatch[1]}`;
            }
            return "@user";
        }
        // Ensure it starts with @
        return mentionText.trim().startsWith("@") ? mentionText.trim() : `@${mentionText.trim()}`;
    });

    // Handle truncated mention spans (spans that were cut off mid-tag)
    // Look for incomplete mention tags and try to extract the mention text from data-value
    const truncatedMentionRegex = /<span[^>]*class=["'][^"']*mention[^"']*["'][^>]*data-value=["']([^"']+)["'][^>]*>/gi;
    text = text.replace(truncatedMentionRegex, (match, dataValue) => {
        return `@${dataValue}`;
    });

    // Also handle cases where we have data-value but the tag is incomplete or in different order
    const dataValueMentionRegex = /data-value=["']([^"']+)["'][^>]*class=["'][^"']*mention/gi;
    text = text.replace(dataValueMentionRegex, (match, dataValue) => {
        return `@${dataValue}`;
    });

    // Handle cases where mention span is cut off but we can still extract data-value
    // This handles cases like: <span class="mention" data-value="jim Franzen" da...
    const partialMentionRegex = /<span[^>]*class=["'][^"']*mention[^"']*["'][^>]*data-value=["']([^"']+)/gi;
    text = text.replace(partialMentionRegex, (match, dataValue) => {
        return `@${dataValue}`;
    });

    // Remove all remaining HTML tags (including incomplete ones)
    text = text.replace(/<[^>]*>/g, "");
    // Also remove any remaining incomplete tags (tags that don't have closing >)
    text = text.replace(/<[^>]*$/g, "");

    // Decode HTML entities (using a simple approach that doesn't require DOM)
    const entityMap = {
        "&nbsp;": " ",
        "&amp;": "&",
        "&lt;": "<",
        "&gt;": ">",
        "&quot;": '"',
        "&#39;": "'",
        "&apos;": "'",
    };
    
    // Replace named entities
    Object.keys(entityMap).forEach(entity => {
        text = text.replace(new RegExp(entity, "gi"), entityMap[entity]);
    });
    
    // Replace numeric entities (&#123; or &#x1F;)
    text = text.replace(/&#(\d+);/g, (match, num) => {
        return String.fromCharCode(parseInt(num, 10));
    });
    text = text.replace(/&#x([0-9A-Fa-f]+);/g, (match, hex) => {
        return String.fromCharCode(parseInt(hex, 16));
    });

    return text.trim();
}